// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useOtChainState } from '@react/redux/store'
import { StageConfiguration } from '@shared/DeploymentConfigTypes'
import { logOT } from '@shared/loggingShared'
import { EthAddr } from '@shared/SchemaTypes'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import LytIssuerLiquidityAssets from './LytIssuerLiquidityAssets'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import Tab from '@mui/material/Tab'
import { ReserveActionPlanStateDataCTO } from '@shared/ReservePlanCTO'
import { fetchReserveActionPlanStateDataCTO } from '@react/lyt/LytFetchers'
import LytIssuerReserveTargetsViewOrEdit from './LytIssuerReserveTargetsViewOrEdit'
import PlanActionCard from './PlanActionCard'
import PlanAdjuster from './PlanAdjuster'
import { ReserveActionPlanClass, ReservePlanModeEnum } from './ReserveActionPlanClass'
import LytExchangeRateCard from './LytExchangeRateCard'

export default function LytIssuerReserves(props: { poolAddr?: EthAddr }): React.ReactElement {
  const mountedPoolAddrRef = useRef('')
  const p = useParams().poolAddr
  const {
    stageConfiguration,
    stageConfiguration: { serverApiBaseUrl, restAPIKey, stageName },
  } = useOtChainState()

  const lytStageConfiguration: StageConfiguration = stageConfiguration

  const lytPoolAddr = (props.poolAddr ? props.poolAddr : p ? p : stageConfiguration.lytPoolAddr) as EthAddr
  const [showData, setShowData] = useState(false)

  const [isLoading, setIsLoading] = useState(true)

  const [reservePlanMode, setReservePlanMode] = useState<ReservePlanModeEnum | undefined>(undefined)
  const [reserveActionPlanStateDataCTO, setReserveActionPlanStateDataCTO] = useState<ReserveActionPlanStateDataCTO>()
  const [reserveActionPlan, setReserveActionPlan] = useState<ReserveActionPlanClass>()
  const [, forceRerender] = React.useState({})

  const fetchReserveActionPlanCTO = async () => {
    setIsLoading(true)
    const reserveActionPlan = await fetchReserveActionPlanStateDataCTO(serverApiBaseUrl, restAPIKey, lytPoolAddr)

    setReserveActionPlanStateDataCTO(reserveActionPlan)
    const reserveActionPlanClass = new ReserveActionPlanClass({
      reserveActionPlanStateDataCTO: reserveActionPlan,
      lytStageConfiguration: lytStageConfiguration,
      reservePlanMode: undefined,
    })
    logOT.debug('LytIssuerReserves rerender change reservePlan 1')
    setReserveActionPlan(reserveActionPlanClass)
    setReservePlanMode(reserveActionPlanClass.reservePlanMode)
    setIsLoading(false)
    forceRerender({})
  }

  const changeReservePlanMode = (mode: ReservePlanModeEnum) => {
    if (reserveActionPlanStateDataCTO) {
      const reserveActionPlanClass = new ReserveActionPlanClass({
        reserveActionPlanStateDataCTO: reserveActionPlanStateDataCTO,
        lytStageConfiguration: lytStageConfiguration,
        reservePlanMode: mode,
      })

      setReserveActionPlan(reserveActionPlanClass)
      setReservePlanMode(mode)
    }
  }

  const redrawnCallback = () => {
    forceRerender({})
  }

  const refreshCallback = () => {
    if (!isLoading) {
      fetchReserveActionPlanCTO()
        .then(() => {
          logOT.debug('LytIssuerReserves rerender refreshCallback 0.3')
        })
        .catch((error) => {
          logOT.error('Error fetching reserves', error)
        })
    }
  }

  useEffect(() => {
    if (lytPoolAddr) {
      if (mountedPoolAddrRef.current !== lytPoolAddr) {
        mountedPoolAddrRef.current = lytPoolAddr
        fetchReserveActionPlanCTO()
          .then(() => {
            logOT.debug('LytIssuerReserves rerender 0.4')
          })
          .catch((error) => {
            logOT.error('Error fetching reserves', error)
          })
      }
    }
  }, [lytPoolAddr])

  if (!reserveActionPlan) {
    return <Box>Loading...</Box>
  }

  const modeWarning =
    reservePlanMode !== reserveActionPlan.defaultPlanMode &&
    (reservePlanMode === ReservePlanModeEnum.InflowMode ||
      (reservePlanMode === ReservePlanModeEnum.RetrieveMode &&
        reserveActionPlan.defaultPlanMode === ReservePlanModeEnum.OutflowMode))

  return (
    <Box data-testid="lyt-issuer-reserves">
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            fetchReserveActionPlanCTO()
          }}
        >
          Reload and Calculate
        </Button>

        <Button variant="contained" size="large" onClick={() => setShowData(!showData)}>
          {showData ? 'Hide Data' : 'Show Data'}
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            const jsonStr = reserveActionPlan.toString()
            const blob = new Blob([jsonStr], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            const now = new Date()
            const gmtStr = now.toUTCString().slice(5, 25).replace(/[ :]/g, '') // Format: DDMMMYYYYHHMMSS GMT
            a.download = `reserve-action-plan-${stageName}-${gmtStr}.json`
            a.click()
            URL.revokeObjectURL(url)
          }}
        >
          Download Plan JSON
        </Button>
      </Box>
      {showData && (
        <Box sx={{ display: 'flex', marginBottom: 2, marginTop: 2, flexDirection: 'column', gap: 1 }}>
          <Typography variant="h3">Action Plan Data Table</Typography>
          <pre>{reserveActionPlan.toString()}</pre>
        </Box>
      )}
      <LytExchangeRateCard />
      <Box
        data-testid="Asset box"
        sx={{ display: 'flex', marginBottom: 2, marginTop: 2, flexDirection: 'column', gap: 1 }}
      >
        <LytIssuerLiquidityAssets key={'s1'} reserveActionPlan={reserveActionPlan} isLoading={isLoading} />
      </Box>
      <TabContext value={reservePlanMode ?? ReservePlanModeEnum.NeutralMode}>
        <Box
          data-testid="Tab Bar"
          sx={{ display: 'flex', flexDirection: 'row', gap: 1, backgroundColor: modeWarning ? '#FFD580' : 'white' }}
        >
          <TabList
            onChange={(_event, value) => changeReservePlanMode(value as ReservePlanModeEnum)}
            aria-label="lab API tabs example"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={ReservePlanModeEnum.NeutralMode} value={ReservePlanModeEnum.NeutralMode} />
            <Tab label={ReservePlanModeEnum.InflowMode} value={ReservePlanModeEnum.InflowMode} />
            <Tab label={ReservePlanModeEnum.RetrieveMode} value={ReservePlanModeEnum.RetrieveMode} />
            <Tab label={ReservePlanModeEnum.OutflowMode} value={ReservePlanModeEnum.OutflowMode} />
          </TabList>
          {modeWarning && (
            <Typography variant="body1" sx={{ color: 'red' }}>
              Warning: The reserve plan mode is not the default mode.
            </Typography>
          )}
        </Box>

        <Box data-testid="Right column">
          <LytIssuerReserveTargetsViewOrEdit
            key={'s2'}
            reserveActionPlanClass={reserveActionPlan}
            refreshCallback={refreshCallback}
          />
        </Box>

        <Typography variant="h3">Action Plan {reserveActionPlan.reservePlanMode} </Typography>
        <PlanAdjuster
          reserveActionPlanClass={reserveActionPlan}
          doRedraw={() => {
            redrawnCallback()
          }}
        />

        {reserveActionPlan.planActions.map((planActionClass, index) => (
          <PlanActionCard
            key={'s1' + '-' + index}
            planActionClass={planActionClass}
            doRefresh={() => {
              refreshCallback()
            }}
            doRedraw={() => {
              redrawnCallback()
            }}
          />
        ))}
      </TabContext>
    </Box>
  )
}
